import React, { useEffect, useState } from 'react'
import { DeleteOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons'
import { Badge, Button, Card, Row, List, Typography, Col, Modal } from 'antd'
import styles from '../Organizations.module.less'
import { CreateUpdateRoleForm } from './CreateUpdateRoleForm'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import { OrganizationActions, OrgModelName } from '~/Store/Organization'
import { getModalVisibility } from '~/Store/Organization/selectors'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { useTranslation } from 'react-i18next'
import { OrgRoleItemResponse } from '~/Services/OrganizationService'
import { OrganizationRoleActions } from '~/Store/OrganizationRoles'
import { Utils } from '~/Utils'

export const RolesCard = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const roles = useAppSelector(state => state.organization.roles)
  const isLoading = useAppSelector(state => state.organization.isLoading)
  const defaultOrg = useAppSelector(getDefaultOrganization)
  const token = useAppSelector(state => state.user.token)
  const clientId = Utils.getClientIdFromToken(token)

  const [selectedId, setSelectedId] = useState<string | undefined>()
  const [selectedRole, setSelectedRole] = useState<OrgRoleItemResponse | null>(
    null,
  )
  const isModalVisible = useAppSelector(
    getModalVisibility(OrgModelName.createUpdateRoleModal),
  )

  useEffect(() => {
    if (defaultOrg?.guid) {
      setSelectedId(undefined)
      dispatch(OrganizationActions.clearRoles())
      dispatch(OrganizationActions.getOrganizationRoles(defaultOrg.guid))
    }
  }, [dispatch, defaultOrg?.guid])

  useEffect(() => {
    if (roles.length > 0 && !selectedId) {
      const firstRole = roles[0]
      setSelectedId(firstRole.id)
      dispatch(OrganizationActions.setOrganizationRoleSelected(firstRole))
    }
  }, [roles, selectedId, dispatch])

  useEffect(() => {
    if (!selectedId || !defaultOrg?.guid) return

    const role = roles.find(r => r.id === selectedId)
    if (!role) return

    dispatch(
      OrganizationRoleActions.getProductClaimsOfRole({
        clientId,
        roleId: selectedId,
      }),
    )
  }, [selectedId, defaultOrg?.guid, clientId, dispatch, roles])

  const showCreateModal = () => {
    setSelectedRole(null)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateRoleModal,
        value: true,
      }),
    )
  }

  const showEditModal = (role: OrgRoleItemResponse) => {
    setSelectedRole(role)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateRoleModal,
        value: true,
      }),
    )
  }

  const handleCancel = () => {
    setSelectedRole(null)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateRoleModal,
        value: false,
      }),
    )
  }

  const handleSubmit = (values: { roleName: string }) => {
    if (!defaultOrg?.guid) return

    if (selectedRole) {
      dispatch(
        OrganizationActions.updateOrganizationRole({
          orgId: defaultOrg.guid,
          oldRoleName: selectedRole.name,
          newRoleName: values.roleName,
        }),
      )
    } else {
      dispatch(
        OrganizationActions.createOrganizationRole({
          orgId: defaultOrg.guid,
          roleNames: [values.roleName],
        }),
      )
    }
  }

  const handleDelete = (role: OrgRoleItemResponse) => {
    if (!defaultOrg?.guid) return

    Modal.confirm({
      title: '',
      content: t('confirmDeleteRole', { roleName: role.name }),
      okText: t('remove'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk: () => {
        dispatch(
          OrganizationActions.deleteOrganizationRole({
            orgId: defaultOrg.guid,
            roleNames: [role.name],
          }),
        )
        if (selectedId === role.id) {
          setSelectedId(undefined)
          dispatch(OrganizationActions.setOrganizationRoleSelected({}))
        }
      },
    })
  }

  return (
    <>
      <Modal
        className={styles.CustomModal}
        title={
          selectedRole
            ? t('roleManagement.editRole')
            : t('roleManagement.createRole')
        }
        visible={isModalVisible}
        width={600}
        onCancel={handleCancel}
        footer={null}
      >
        <CreateUpdateRoleForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialData={selectedRole}
          isLoading={isLoading}
        />
      </Modal>
      <Card
        title={
          <Row align="middle" gutter={8}>
            <Col>
              <Typography.Text>{t('roleManagement.roleList')}</Typography.Text>
            </Col>
            <Col>
              <Badge count={roles.length} />
            </Col>
          </Row>
        }
        extra={
          <Button
            icon={<PlusOutlined style={{ fontSize: 14 }} />}
            size="small"
            className={styles.AddButton}
            onClick={showCreateModal}
          />
        }
        className={styles.RolesCard}
        bodyStyle={{ paddingTop: 0 }}
      >
        <List
          dataSource={roles}
          renderItem={item => {
            const selected = selectedId === item.id

            return (
              <List.Item
                className={`${styles.RoleItem} ${
                  selected ? styles.RoleItemSelected : ''
                }`}
                onClick={() => {
                  setSelectedId(item.id)
                  dispatch(OrganizationActions.setOrganizationRoleSelected(item))
                }}
                actions={[
                  <Button
                    key="edit"
                    type="link"
                    icon={<EditOutlined />}
                    onClick={e => {
                      e.stopPropagation()
                      showEditModal(item)
                    }}
                  />,
                  <Button
                    key="delete"
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={e => {
                      e.stopPropagation()
                      handleDelete(item)
                    }}
                  />,
                ]}
              >
                <Typography.Text style={{ paddingInline: 12 }}>
                  {item.name}
                </Typography.Text>
              </List.Item>
            )
          }}
        />
      </Card>
    </>
  )
}
